<template>
	<div
		:class="[
			$style['app-select'],
			{
				[$style['app-select--disabled']]: isDisabled,
				[$style['app-select--focused']]: isFocused,
				[$style['app-select--empty']]: !options.length
			}
		]"
	>
		<select
			:class="$style['app-select__input']"
			:value="privateValue"
			@focus="inputFocusHandler"
			@blur="inputBlurHandler"
			@input="inputInputHandler"
		>
			<option
				v-for="option in options"
				:key="option.value"
				:value="option.value"
				:disabled="option.disabled"
			>
				{{ option.text }}
			</option>
		</select>
		<FloatingLabel
			:label-text="labelText"
			:required="isRequired"
			:is-focused="isFocused"
			:is-floated="isFloated"
		/>
		<NotchedOutline
			:class="$style['app-text-field__outline']"
			:is-focused="isFocused"
			:is-floated="isFloated && !!labelText"
		/>
		<NotchedOutlineIdle
			:class="$style['app-text-field__idle']"
			:is-focused="isFocused"
			:is-floated="isFloated && !!labelText"
		/>
	</div>
</template>
<script>
import NotchedOutline from '@c:essentials/NotchedOutline'
import NotchedOutlineIdle from '@c:essentials/NotchedOutlineIdle'
import FloatingLabel from '@c:essentials/FloatingLabel'
import requiredField from '@/mixins/requiredField'
export default {
	name: 'BaseSelectInput',
	components: {
		NotchedOutline,
		NotchedOutlineIdle,
		FloatingLabel
	},
	mixins: [requiredField],
	props: {
		value: {
			type: [String, Number],
			default: ''
		},
		labelText: {
			type: String,
			default: ''
		},
		required: {
			type: [Boolean, String],
			default: false
		},
		options: {
			type: Array,
			required: true
		}
	},
	data: () => ({
		privateValue: null,
		isFocused: false,
		isFloated: false
	}),
	watch: {
		value: {
			immediate: true,
			handler (val) {
				this.privateValue = val
				if (!this.isFocused) {
					this.isFloated = !!val
				}
			}
		},
		options () {
			if (!this.isFocused) {
				this.privateValue = ''
				this.isFloated = false
			}
		}
	},
	methods: {
		inputFocusHandler (event) {
			this.isFocused = true
			this.isFloated = true
			this.$emit('focus', event)
		},
		inputBlurHandler (event) {
			this.isFocused = false
			this.isFloated = !!this.value
			this.$emit('blur', event)
		},
		inputInputHandler (event) {
			this.$emit('input', event.target.value)
		}
	}
}
</script>
<style lang="scss" module>
.app-select {
    position: relative;
    display: inline-flex;
    width: 100%;
    align-items: flex-end;
    box-sizing: border-box;
    margin: $gutter*1.25 0;
    height: 48px;
    border: none;

    // Use pseudo-element with mask for theme-adaptive dropdown icon
    &::after {
        content: "";
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        width: 10px;
        height: 5px;
        background-color: getColor(grey);
        mask-image: url("data:image/svg+xml;charset=utf-8,%3Csvg width='10' height='5' viewBox='7 10 10 5' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath fill-rule='evenodd' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
        mask-size: contain;
        mask-repeat: no-repeat;
        mask-position: center;
        pointer-events: none;
        z-index: 2;
        transition: transform 0.2s ease-in-out;
    }

    &__label {
        z-index: 1;
    }

    &__input {
        padding: 12px 26px 10px 16px;
        display: flex;
        border: none;
        background-color: getColor(input-background);
        color: getColor(text);
        z-index: 1;
        line-height: 1.75rem;
        font-size: 16px;
        width: 100%;
        outline: none;
        white-space: nowrap;
        cursor: pointer;
        appearance: none;

        &:disabled {
            background-color: getColor(input-background-disabled);
            color: getColor(disabled-text);
            cursor: not-allowed;
        }
    }

    &--disabled {
        .app-select__label {
            color: getColor(input-label-disabled);
        }

        &::after {
            opacity: 0.38;
        }
    }

    &--empty::after {
        display: none;
    }

    &--focused::after {
        transform: translateY(-50%) rotate(-180deg);
    }
}
</style>
